import React, { useEffect } from "react";
import { Redirect, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { parse } from "query-string";

import I18n from "../../I18n";
import theme from "../../style/_theme.json";

import { mapProperties, hydrateRoute, number, parseFilter } from "colbi_web_ui/lib/utils";
import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    uploadAction,
    toggleMenu,
    setLocale,
    auditNoteSubmit,
    removeModal,
    getColbiDdEntities,
} from "../../store/actions";

import { List, Filters, LoadingIndicator } from "colbi_web_ui/lib/components";
import { SovosPageLayout, SovosPageContentGeneric } from "mosaic-react";

import { EntityCard } from "colbi_web_ui/lib/components/rows";
import { FloatButton } from "colbi_web_ui/lib/components/inputs";
import { generateSettingsMenuItems } from "../../components/GlobalLayout/menuBuilderUtils";

import styles from "./Settings.module.sass";

import {
    decodeUserSession,
    buildStaticLinks,
    redirectNotValid,
    BuildSovosNavProps,
} from "../../utils/utils";

import GlobalLayout from "../../components/GlobalLayout/GlobalLayout";
import { useAuth } from "react-oidc-context";
import { useSoviAi } from "../../contexts/SoviAiContext";

const FloatCreateButton = ({ typename, i18n, pushModal, reload }) => {
    const { hasSoviAi } = useSoviAi();

    if (hasSoviAi === null) return null;

    return (
        <div className={hasSoviAi ? styles["float-button-wrapper--sovi"] : styles["float-button-wrapper"]}>
            <FloatButton
                className={styles["float-button"]}
                icon={`new-${(typename || "").toLowerCase()}`}
                onClick={() => {
                    pushModal({
                        title: i18n`New ${typename}`,
                        view: `${typename}Form`,
                        data: { __typename: typename },
                    })
                        .then(() => {
                            reload && reload();
                        })
                        .catch(() => null);
                }}
            />
        </div>
    );
};

const Settings = (props) => {
    const { user, fetch, match, setLocale, location, history, pushModal, reload } = props;

    const locale = match.params.locale || "en";
    const i18n = I18n.use(locale);
    const auth = useAuth();

    const headerTitle =
        match.params.section === "kpis"
            ? `settings_menu_${match?.params?.section || ""}`
            : `${match?.params?.section || ""}`;

    useEffect(() => {
        setLocale(locale);
    }, [locale, setLocale]);

    const userInformation = decodeUserSession(user);

    const menuItems = generateSettingsMenuItems(userInformation?.role, history, locale, i18n) || [];
    let navigation = menuItems;

    const staticLinks = buildStaticLinks(navigation, history, match.params, i18n);

    const redirect = redirectNotValid(userInformation, locale, true, auth);

    if (redirect) {
        return redirect;
    }

    if (!(userInformation && userInformation.accessSettings)) {
        return <Redirect to={`/${locale}/unauthorized`} />;
    }

    (navigation || []).forEach((elem) => {
        elem.name = I18n.translate`${elem.name}`;
        return elem;
    });

    const queryParams = parse(location.search) || {};
    const routeParams = match.params || {};
    const params = {
        ...queryParams,
        ...routeParams,
    };

    const data = mapProperties(
        {
            items: "query(0).list",
            header: "query(0).header",
            footer: "query(0).footer",
            metadata: "query(0).metadata",
            page: "param(page)",
            status: "status(0)",
            pageSize: "param(pageSize)",
            pageDefault: 10,
            filter: "param(filter)",
            sort: "param(sort)",
            availableFilters: "query(0).filters",
        },
        (fetch || {}).results,
        (fetch || {}).status,
        (Settings.queries && Settings.queries(params)) || [],
        props,
        params,
        i18n,
        locale,
    );

    const currentSection = match.params.section || "/";
    let floatCreateTypename = (navigation.find(({ section }) => section === currentSection) || {})
        .createType;

    if (currentSection === "/") {
        const basePath = "/settings/:organizationId?";
        const hydrated = hydrateRoute(basePath, { ...params });
        return <Redirect to={`${hydrated}${navigation[0]?.section || "projects"}`} replace />;
    }

    const navProps = BuildSovosNavProps(
        undefined,
        [],
        { name: "" },
        {},
        userInformation,
        staticLinks || [],
    );

    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries: "",
        params,
        staticLinks,
        styles,
    };


    return (
        <GlobalLayout {...globalLayoutProps}>
            <SovosPageLayout pageTitle={i18n`${headerTitle}`}>
                <SovosPageContentGeneric sx={{ marginLeft: "24px", marginRight: "24px" }}>
                    <>
                        {fetch.activity && (
                            <LoadingIndicator className={`${styles["loading-indicator"]}`} />
                        )}
                        {floatCreateTypename ? (
                            <FloatCreateButton
                                typename={floatCreateTypename}
                                i18n={i18n}
                                pushModal={pushModal}
                                reload={reload}
                            />
                        ) : null}
                        <Filters
                            filter={data.filter}
                            availableFilters={data.availableFilters}
                            stickyTop={theme}
                            anchor="results"
                        />
                        <List
                            modifiers={["--root-margin-top-small", "--row-style-alternate"]}
                            {...(data || {})}
                            card={EntityCard}
                            onClick={(e) => {
                                e.originalEvent.preventDefault();
                                e.originalEvent.stopPropagation();
                                const { id, card, __typename } = e.target.value || {};
                                const title =
                                    __typename === "TranslationTable"
                                        ? i18n`Configuration`
                                        : card && card[0] && card[0][0]
                                            ? card[0][0].value
                                            : "";
                                pushModal({
                                    title,
                                    view: `${__typename}Form`,
                                    args: { id },
                                })
                                    .then(() => {
                                        history.push(match.url);
                                        reload && reload();
                                    })
                                    .catch(() => null);
                            }}
                        />
                    </>
                </SovosPageContentGeneric>
            </SovosPageLayout>
        </GlobalLayout>
    );
};

Settings.queries = (args) => {
    const { section, pageSize, page, filter, locale } = args || {};
    const pagesWithLocale = [
        "nc_rules",
        "packages",
        "kpis",
        "custom_reports",
        "translations_table",
    ];

    return section
        ? [
            {
                resource: section,
                args: {
                    pageSize: number(pageSize),
                    page: page ? number(page) : 0,
                    locale: pagesWithLocale.includes(section) ? locale : undefined,
                    filter: filter
                        ? { type: "[FilterInput!]", value: parseFilter(filter) }
                        : undefined,
                    sort: args.sort
                        ? { type: "[SortInput!]", value: parseFilter(args.sort) }
                        : undefined,
                },
                body: `__typename, items{...${section}},hasMore,total,filters{ prop, name, type, list, disableOperators }`,
            },
        ]
        : [];
};

export default connect(
    ({ user, fetch, menu, uploads }) => ({
        user,
        fetch,
        menu,
        uploads,
    }),
    {
        toggleMenu,
        logout,
        setLocale,
        pushModal,
        popModal,
        removeModal,
        pushDialog,
        uploadFiles: uploadAction,
        submitAuditNote: auditNoteSubmit,
        getColbiDdEntities,
    },
)(withRouter(Settings));
